<template>
  <div style="min-height: 600px;">
    <PageHeader style="position: relative;" :title="displayName" no-padding :loading="!loadingHasFinish" />
    <transition-group name="fade" mode="out-in">
      <v-alert key="alert" outline class="my-0" type="error" :value="loadingHasFinish && !loading && !currentMonitoringFeature">
        <div class="primary--text">
          Oops, we couldn't load this location. Try again in a few minutes - let us know if it happens again!
        </div>
      </v-alert>
      <PageLoader key="pageLoader" style="position: absolute; padding: 150px 0;" v-if="loading" resourceName="location data" />
      <div key="main-container" v-else-if="!loading && currentMonitoringFeature">
        <v-tabs v-model="tab" active-class="tabs-active-tab" slider-color="info" dark grow centered>
          <v-tab href="#tab-summary" id="mf-summary">
            <v-icon class="mr-1">assignment</v-icon>Summary
          </v-tab>
          <v-tab href="#tab-time-series" v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.continuousAquarius]">
            <v-icon class="mr-1">mdi-clock</v-icon>Time Series
          </v-tab>
          <v-tab href="#tab-sample-analyses" v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.discreteEnvirodata]" id="mf-discrete-data">
            <v-icon class="mr-1">mdi-water</v-icon>Sample Analyses
          </v-tab>
          <v-tab href="#tab-graph-dash" v-if="showTabs.graphDashboard && currentFeatureDataSourceIndexByType[SOURCE_TYPES.discreteEnvirodata]" id="mf-discrete-graph-dashboard">
            <v-icon class="mr-1">poll</v-icon>Graph Dashboard
          </v-tab>
          <v-tab href="#tab-specimen-info" v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.vegetativeMonitoringEnvirodata]" id="mf-vegetativemonitoring-specimen-info">
            <v-icon class="mr-1">poll</v-icon>Specimen Info
          </v-tab>
          <v-tab href="#tab-plot-survey" v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.vegetativeMonitoringEnvirodata]" id="mf-vegetativemonitoring-plot-query">
            <v-icon class="mr-1">poll</v-icon>Plot Survey Query
          </v-tab>
          <v-tab href="#tab-specimen-survey" v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.vegetativeMonitoringEnvirodata]" id="mf-vegetativemonitoring-specimen-query">
            <v-icon class="mr-1">poll</v-icon>Specimen Survey Query
          </v-tab>
          <!-- Survey related tabs -->
          <v-tab href="#tab-survey-specimen-info" v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.surveysEnvirodata]" id="mf-survey-specimen-info">
            <v-icon class="mr-1">poll</v-icon>Survey Specimen Info
          </v-tab>
          <v-tab href="#tab-survey-location-query" v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.surveysEnvirodata]" id="mf-survey-location-query">
            <v-icon class="mr-1">poll</v-icon>Survey Location Query
          </v-tab>
          <v-tab href="#tab-survey-specimen-query" v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.surveysEnvirodata]" id="mf-survey-specimen-query">
            <v-icon class="mr-1">poll</v-icon>Survey Specimen Query
          </v-tab>
          <!-- End of Survey related tabs -->
          
          <v-tab href="#tab-photos" v-if="photos.length > 0">
            <v-icon class="mr-1">image</v-icon>Photos
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item value="tab-summary">
            <Summary />
          </v-tab-item>
          <v-tab-item value="tab-time-series">
            <ContinuousAquariusData v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.continuousAquarius]" />
          </v-tab-item>
          <v-tab-item value="tab-sample-analyses">
            <DiscreteEnviroData v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.discreteEnvirodata]" />
          </v-tab-item>
          <v-tab-item value="tab-graph-dash">
            <DiscreteDataGraphDashboard v-if="showTabs.graphDashboard && currentFeatureDataSourceIndexByType[SOURCE_TYPES.discreteEnvirodata]" />
          </v-tab-item>
          <v-tab-item value="tab-specimen-info">
            <VegetativeMonitoringSpecimenInfo v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.vegetativeMonitoringEnvirodata]" />
          </v-tab-item>
          <v-tab-item value="tab-plot-survey">
            <VegetativeMonitoringPlotSurvey v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.vegetativeMonitoringEnvirodata]" />
          </v-tab-item>
          <v-tab-item value="tab-specimen-survey">
            <VegetativeMonitoringSpecimenSurvey v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.vegetativeMonitoringEnvirodata]" />
          </v-tab-item>

          <v-tab-item value="tab-survey-specimen-info">
            <SurveySpecimenInfo v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.surveysEnvirodata]" />
          </v-tab-item>
          <v-tab-item value="tab-survey-location-query">
            <SurveyLocationSurvey v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.surveysEnvirodata]" />
          </v-tab-item>
          <v-tab-item value="tab-survey-specimen-query">
            <SurveySpecimenSurvey v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.surveysEnvirodata]" />
          </v-tab-item>

          <v-tab-item value="tab-photos" v-if="photos.length > 0">
            <Photos v-if="currentFeatureDataSourceIndexByType[SOURCE_TYPES.continuousAquarius]" :photos="photos" :stationName="displayName" />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </transition-group>
  </div>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'

  const app = createNamespacedHelpers('app')
  const tutorial = createNamespacedHelpers('tutorial')
  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const navigation = createNamespacedHelpers('navigation')
  const uiConfig = createNamespacedHelpers('uiconfig')

  import Summary from './Summary/Summary'

  import DiscreteEnviroData from './DataSources/DiscreteEnviroData/Tabs/DiscreteEnviroData'
  import DiscreteDataGraphDashboard from './DataSources/DiscreteEnviroData/Tabs/DiscreteDataGraphDashboard'

  import ContinuousAquariusData from './DataSources/ContinuousAquariusData/Tabs/ContinuousAquariusData'

  import SampleActivities from './DataSources/SampleActivities/SampleActivities'

  import VegetativeMonitoringSpecimenInfo from './DataSources/VegetativeMonitoringEnviroData/Tabs/VegetativeMonitoringSpecimenInfo'
  import VegetativeMonitoringPlotSurvey from './DataSources/VegetativeMonitoringEnviroData/Tabs/VegetativeMonitoringPlotSurvey'
  import VegetativeMonitoringSpecimenSurvey from './DataSources/VegetativeMonitoringEnviroData/Tabs/VegetativeMonitoringSpecimenSurvey'

  import SurveySpecimenInfo from './DataSources/SurveyEnvirodata/Tabs/SurveySpecimenInfo'
  import SurveyLocationSurvey from './DataSources/SurveyEnvirodata/Tabs/SurveyLocationSurvey'
  import SurveySpecimenSurvey from './DataSources/SurveyEnvirodata/Tabs/SurveySpecimenSurvey'

  import Photos from './DataSources/Photos/Photos'

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  export default {
    components: {
      ContinuousAquariusData,
      DiscreteEnviroData,
      DiscreteDataGraphDashboard,
      SampleActivities,
      Summary,
      Photos,
      VegetativeMonitoringSpecimenInfo,
      VegetativeMonitoringPlotSurvey,
      VegetativeMonitoringSpecimenSurvey,
      SurveySpecimenInfo,
      SurveyLocationSurvey,
      SurveySpecimenSurvey,
    },
    props: {
      // It's a number when routed from vue, and a string when routed from the url
      monitoringFeatureId: [Number, String],
    },
    data() {
      return {
        SOURCE_TYPES,
        isLoadingSampleData: false,
        isMetaDataLoaded: false,
        tab: 'tab-summary',
        alert: false,
        loading: false,
      }
    },
    computed: {
      ...app.mapGetters(['loadingHasFinish']),
      ...tutorial.mapGetters(['showIntro']),
      ...monitoringFeatures.mapGetters([
        'currentMonitoringFeature',
        'currentFeatureDataSourceIndexByType',
        'getDisplayName',
        'currentFeatureDataSourceData',
        'monitoringFeaturesIndex',
      ]),
      ...uiConfig.mapGetters(['uiConfigIndex']),
      displayName() {
        if (!this.monitoringFeatureId) return 'LOCATION NOT FOUND'
        let mf = this.monitoringFeaturesIndex[this.monitoringFeatureId]
        return this.getDisplayName(mf)
      },
      continuousData() {
        return this.currentFeatureDataSourceData(SOURCE_TYPES.continuousAquarius)
      },
      photos() {
        return this.continuousData && this.continuousData.photos || []
      },
      stationName() {
        let ds = this.currentFeatureDataSourceIndexByType[SOURCE_TYPES.continuousAquarius]
        return ds && ds.SourceName
      },
      showTabs() {
        let graphDashboard = this.uiConfigIndex['StationGraphTabGlobalVisibility']
        graphDashboard = graphDashboard && JSON.parse(graphDashboard)

        return {
          graphDashboard,
        }
      },
    },
    watch: {
      loadingHasFinish: {
        immediate: true,
        handler(value) {
          if (value) this.fetch()
        },
      },
    },
    methods: {
      ...navigation.mapActions(['saveToRecentLocations']),
      ...tutorial.mapActions(['end']),
      ...monitoringFeatures.mapActions(['fetchMonitoringFeatureDetails', 'setCurrentMonitoringFeature']),
      async fetch() {
        this.loading = true

        try {
          let monitoringFeatureDetails = await this.fetchMonitoringFeatureDetails({
            monitoringFeatureId: this.monitoringFeatureId,
          })

          this.setCurrentMonitoringFeature({
            monitoringFeatureDetails,
            monitoringFeatureId: this.monitoringFeatureId,
          })
        } catch (error) {
          console.error(error)
        }

        this.loading = false
      },
    },
    mounted() {
      this.saveToRecentLocations({
        monitoringFeatureId: this.monitoringFeatureId,
      })
    },
  }
</script>